import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { HeroDashboardMockup } from '@/components/HeroDashboardMockup';
import { TutorRequestSection } from '@/components/TutorRequestSection';
import { FeatureDetailSection } from '@/components/FeatureDetailSection';
import { FeedbackFeatureSection } from '@/components/FeedbackFeatureSection';
import { CourseHookStrip } from '@/components/marketing/CourseHookStrip';
import { RoleAnswers, RoleClose, RoleHead } from '@/components/marketing/roleKit';
import { COURSE_HREF, MARKETING_CTAS } from '@/lib/marketingCtas';

export const STUDENTS_ANSWERS = [
  {
    q: 'Can I pick my tutor?',
    a: 'You tell us the subject and what you need, and Mentara matches you with an available tutor. After your first session you can ask for the same tutor again.',
  },
  {
    q: 'What does tutoring cost?',
    a: 'You see the amount for each session in your payments page, and you pay by card once the session is complete. Cancel before a session happens and it is not charged.',
  },
  {
    q: 'Do I need a tutor to use the SAT course?',
    a: (
      <>
        No. The <Link to={COURSE_HREF} className="underline underline-offset-4">free SAT course</Link> works without an account or a tutor. A tutor is there if you want a person alongside it.
      </>
    ),
  },
  {
    q: 'Can my parent see my sessions?',
    a: 'Only if you link them. You create the invite, choose what they can see, and can revoke the link at any time.',
  },
  {
    q: 'What subjects can I get help with?',
    a: 'SAT prep and a range of school subjects. Choose your subject in the request form to see what is on offer right now.',
  },
] as const;

export const StudentsLandingPage = () => (
  <>
    <RoleHead
      title="Online Tutoring for Students | Homework Help & Test Prep | Mentara"
      description="One-on-one online tutoring with homework help and test prep, matched to you. Plus a free SAT course that needs no account."
      keywords="online tutoring, homework help, test prep, 1-on-1 tutoring, personalized tutoring, improve grades, free SAT course, student tutoring"
      path="/what-we-do/students/"
    />

    {/* Starting Line: centred, with the student dashboard rising from the fold. */}
    <section aria-labelledby="students-title" className="overflow-hidden">
      <div className="mx-auto flex max-w-3xl flex-col items-center gap-5 px-4 pb-10 pt-12 text-center sm:px-6 lg:pt-16">
        <h1 id="students-title" className="text-balance text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
          Find a tutor who fits your week.
        </h1>
        <p className="max-w-xl text-pretty text-lg leading-relaxed text-muted-foreground">
          Tell us the subject and what you need. We match you with a tutor, you agree a time, and you meet online. Every session ends with written notes.
        </p>
        <Button asChild size="lg" className="mt-1 h-12 w-full px-8 text-base sm:w-auto">
          <Link to={MARKETING_CTAS.students.href}>{MARKETING_CTAS.students.label}</Link>
        </Button>
      </div>
      <figure className="mx-auto w-full max-w-4xl px-4 sm:px-6">
        <div
          className="max-h-[22rem] overflow-hidden rounded-t-2xl [mask-image:linear-gradient(to_bottom,black_70%,transparent)] sm:max-h-[26rem]"
          data-testid="hero-artifact"
        >
          <HeroDashboardMockup />
        </div>
        <figcaption className="mt-2 text-center text-sm text-muted-foreground">Example student dashboard.</figcaption>
      </figure>
    </section>

    <CourseHookStrip audience="students" />
    <TutorRequestSection />
    <FeatureDetailSection />
    <FeedbackFeatureSection />
    <RoleAnswers audience="students" answers={STUDENTS_ANSWERS} />
    <RoleClose
      audience="students"
      heading="Ready when you are."
      body="Create a free account, send a request, and we will find you a tutor."
      primary={MARKETING_CTAS.students}
      secondary={{ label: 'How tutoring works', to: '/how-it-works/students/' }}
    />
  </>
);
